// src/pages-components/MangaPage/MangaPage.js
import { useState, useEffect } from 'react';
import Head from 'next/head';
import stylesMobile from './MangaPageMobile.module.css';
import stylesDesktop from './MangaPageDesktop.module.css';
import MangaMainSection from './components/MangaMainSection/MangaMainSection';
import MangaChapters from './components/MangaChapters/MangaChapters';
import MangaButton from './components/MangaButton/mangaButton.js';
import { clientLog } from "../../../logger/clientLogger";
import { useRouter } from 'next/router';
import AdvertBanner from '../../components/AdvertBanner/AdvertBanner.js';

const MetaTags = ({ mangaData, error, currentUrl }) => {
  if (error || !mangaData) {
    return (
      <Head>
        <title>{error ? 'Ошибка загрузки | ManGO' : 'Манга не найдена | ManGO'}</title>
        <meta name="description" content={error ? 'Не удалось загрузить информацию о манге' : 'Запрошенная манга не найдена'} />
        <meta property="og:type" content="book" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={error ? 'Ошибка загрузки | ManGO' : 'Манга не найдена | ManGO'} />
        <meta property="og:description" content={error ? 'Не удалось загрузить информацию о манге' : 'Запрошенная манга не найдена'} />
        <meta property="og:image" content="/logo/logo_mini_256_256.png" />
        <meta property="og:site_name" content="ManGO" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content={error ? 'Ошибка загрузки | ManGO' : 'Манга не найдена | ManGO'} />
        <meta property="twitter:description" content={error ? 'Не удалось загрузить информацию о манге' : 'Запрошенная манга не найдена'} />
        <meta property="twitter:image" content="/logo/logo_mini_256_256.png" />
      </Head>
    );
  }

  const pageTitle = `${mangaData.title}${mangaData.alternative_titles?.[0] ? ` (${mangaData.alternative_titles[0]})` : ''} | ManGO`;
  const pageDescription = mangaData.description || `Читайте мангу ${mangaData.title} онлайн. ${mangaData.genres?.join(', ')}. Рейтинг: ${mangaData.avg_rating}`;
  const coverImage = mangaData.cover_image?.core_url ||
                    mangaData.cover_image?.selectel_url ||
                    mangaData.cover_url ||
                    '/logo/logo_mini_256_256.png';

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta property="og:type" content="book" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={coverImage} />
      <meta property="og:site_name" content="ManGO" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={coverImage} />
      <link rel="canonical" href={currentUrl} />
    </Head>
  );
};

const MangaPage = ({ mangaData, currentUrl }) => {
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(null);
  const [activeTab, setActiveTab] = useState('main');
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState(null);

  // Определяем мобильный режим
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    if (typeof window !== 'undefined') {
      checkIsMobile();
      window.addEventListener('resize', checkIsMobile);
      return () => window.removeEventListener('resize', checkIsMobile);
    }
  }, []);

  // Подгружаем главы только когда есть project_id
  useEffect(() => {
    if (!mangaData?.project_id) {
      return;
    }
    setChaptersLoading(true);

    fetch(`${process.env.NEXT_PUBLIC_API_URL || ""}/api/v2/chapters/project/${mangaData.project_id}`)
      .then(res => {
        if (!res.ok) {
          clientLog("warn", `[MangaPage] Ошибка загрузки глав. status=${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setChapters(data);
      })
      .catch(err => {
        clientLog("error", `[MangaPage] Ошибка при загрузке глав: ${err.message}`);
        setChaptersError('Не удалось загрузить главы');
      })
      .finally(() => setChaptersLoading(false));
  }, [mangaData?.project_id]);

  if (isMobile === null) {
    return null;
  }

  // ✅ Определяем стили после того, как isMobile известен
  const styles = isMobile ? stylesMobile : stylesDesktop;

  // Находим минимальную главу
  const minChapter = chapters.length
    ? chapters.reduce((min, ch) => (ch.chapter_number < min.chapter_number ? ch : min), chapters[0])
    : null;

  const backgroundStyle = {
    background: `
      linear-gradient(180deg, rgba(24,24,24,0) 5%, rgba(24,24,24,0.46) ${isMobile ? '60%' : '30%'}, rgba(24,24,24,0.73) ${isMobile ? '70%' : '40%'}, rgba(24,24,24,1) ${isMobile ? '80%' : '70%'}),
      url(${mangaData.cover_image?.core_url ||
            mangaData.cover_image?.selectel_url ||
            mangaData.cover_url ||
            '/logo/logo_mini_256_256.png'}) center / cover
    `,
    filter: isMobile ? 'blur(10px) brightness(0.5)' : 'blur(15px)',
  };

  if (isMobile === null) {
    return null; // пока не определено, ничего не рендерим
  }

  return (
    <>
      <MetaTags mangaData={mangaData} currentUrl={currentUrl} />

      {isMobile ? (
        <>
        <div className={styles.manga_cont}>
          <div className={styles.manga_first_cont}>
            <div className={styles.back_cover} style={backgroundStyle} />
            <img className={styles.manga_cover} alt={mangaData.title} src={mangaData.cover_image?.core_url ||
                                                                          mangaData.cover_image?.selectel_url ||
                                                                          mangaData.cover_url ||
                                                                          '/logo/logo_mini_256_256.png'} />
            <div className={styles.types}>
              {mangaData.genres.slice(0, 3).map((genre, i) => (
                <p key={i} className={styles.type}>{genre}</p>
              ))}
            </div>
            <p className={styles.manga_name}>{mangaData.title}</p>
            {mangaData.alternative_titles?.[0] && (
              <p className={styles.second_manga_name}>{mangaData.alternative_titles[0]}</p>
            )}
            <div className={styles.manga_rate_voutes}>
              <p>
                <span className={styles.manga_rate}>{mangaData.avg_rating}</span> | {mangaData.total_likes} голосов
              </p>
            </div>
            <div className={styles.manga_info_slider}>
              {['main', 'chapters'].map((tab) => (
                <p
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={activeTab === tab ? styles.active : ''}
                >
                  {tab === 'main' ? 'О тайтле' : 'Главы'}
                </p>
              ))}
            </div>
          </div>
          <div className={styles.manga_second_cont}>
            {activeTab === 'main' && <><MangaMainSection manga={mangaData} /></>}
            {activeTab === 'chapters' && (
              <>
                {chaptersLoading && <div className={styles.statusMessage}>
                    <div className={styles.statusContent}>
                      <video
                        style={{
                          maxWidth: "100px", 
                          maxHeight: "100px", 
                          backgroundColor: "transparent !important"
                        }} 
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      >
                        <source src="/logo/favicon2.webm" type="video/webm" />
                        <source src="/logo/favicon2.mov" type="video/quicktime" />
                      </video>
                      <p style={{transform: "translateY(-10px)", marginBottom: "0px"}}>Загрузка...</p>
                    </div>
                  </div>}
                {chaptersError && <p style={{ color: 'red' }}>{chaptersError}</p>}
                {!chaptersLoading && !chaptersError && <MangaChapters mangaid={mangaData.id} chapters={chapters} />}
              </>
            )}
          </div>
        </div>
        {activeTab === 'main' && minChapter && (
          <MangaButton
            project_title={mangaData.transliterated_name}
            volume={minChapter.volume_number}
            chapter={minChapter.chapter_number}
            mangaid={mangaData.id}
          />
        )}
      </>
      ) : (
        <div className={styles.mangaCont}>
          <div className={styles.mangaBackCover} style={backgroundStyle} />
          <div className={styles.mangaCoverWrapper}>
            <div className={styles.mangaCover}>
              <img className={styles.cover} src={mangaData.cover_image?.core_url ||
                                                  mangaData.cover_image?.selectel_url ||
                                                  mangaData.cover_url ||
                                                  '/logo/logo_mini_256_256.png'} alt="" />
              <AdvertBanner/>
            </div>
            <div className={styles.mangaInfoCont}>
              <p className={styles.mangaHeader}>
                <span className={styles.mangaName}>{mangaData.title}</span>
                <span className={styles.mangaRate}>{mangaData.rate}</span>
              </p>
              {mangaData.alternative_titles?.[0] && (
                <p className={styles.second_manga_name}>{mangaData.alternative_titles[0]}</p>
              )}
              <p className={styles.mangaMeta}>
                {mangaData.genres.map((genre, index) => (
                  <span key={index} className={styles.mangaTags}>{genre}</span>
                ))}
              </p>
              <div className={styles.manga_info_slider}>
                {['main', 'chapters'].map((tab) => (
                  <p 
                    key={tab}
                    onClick={() => setActiveTab(tab)} 
                    className={activeTab === tab ? styles.active : ''}
                  >
                    {tab === 'main' ? 'О тайтле' : tab === 'chapters' ? 'Главы' : 'Вернуться на главную'}
                  </p>
                ))}
                <p onClick={() => router.push('/')}>Вернуться на главную</p>
              </div>
              <div className={styles.mangaSecondCont}>
                {activeTab === 'main' && <MangaMainSection manga={mangaData} />}
                {activeTab === 'chapters' && (
                  <>
                    {chaptersLoading && <div className={styles.statusMessage}>
                    <div className={styles.statusContent}>
                      <video style={{maxWidth: "100px", maxHeight: "100px", backgroundColor: "transparent !important"}} autoPlay loop muted playsInline src="/logo/favicon2.webm"></video>
                      <p style={{transform: "translateY(-10px)", marginBottom: "0px"}}>Загрузка...</p>
                    </div>
                  </div>}
                    {chaptersError && <p style={{ color: 'red' }}>{chaptersError}</p>}
                    {!chaptersLoading && !chaptersError && <MangaChapters mangaid={mangaData.id} chapters={chapters} />}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MangaPage;
